import { useEffect, useRef, useState } from "preact/hooks";
import { install } from "../pwa/launch";
import type { Installed } from "../pwa/state";
import { formatBytes, type DownloadProgress } from "../pwa/update";

/**
 * The install screen (issue #3): every Pack shard and the catalogue files downloaded for
 * offline use, with per-Pack progress and the total, before the Character list can show.
 * A failure stops with the reason and a Retry; nothing here dismisses the screen early. A
 * download interrupted by closing the app resumes with the missing files on the next launch.
 */
export function InstallScreen({ onInstalled }: { onInstalled: (active: Installed) => void }) {
  const [progress, setProgress] = useState<DownloadProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const latest = useRef<DownloadProgress | null>(null);
  const frame = useRef(0);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    const onProgress = (p: DownloadProgress) => {
      latest.current = p;
      if (frame.current) return;
      frame.current = requestAnimationFrame(() => {
        frame.current = 0;
        if (!cancelled) setProgress(latest.current);
      });
    };
    install(onProgress).then(
      (active) => {
        if (cancelled) return;
        setProgress(latest.current);
        onInstalled(active);
      },
      (e: unknown) => {
        if (!cancelled) setError((e as Error).message);
      },
    );
    return () => {
      cancelled = true;
      if (frame.current) cancelAnimationFrame(frame.current);
    };
  }, [attempt]);

  const pct = progress && progress.bytes > 0 ? Math.floor((progress.done / progress.bytes) * 100) : 0;
  return (
    <div class="install" role="dialog" aria-modal="true" aria-labelledby="install-title">
      <div class="install-panel">
        <h1 id="install-title">Pathfinder Offline</h1>
        <p>Downloading the Snapshot so every Pack works without a connection. Keep the app open until it finishes.</p>
        {progress ? (
          <>
            <div class="install-total">
              <progress max={progress.bytes} value={progress.done} aria-label="Total download" />
              <p>
                <strong>{pct}%</strong> · {formatBytes(progress.done)} of {formatBytes(progress.bytes)} · {progress.filesDone} of {progress.files} files
              </p>
            </div>
            <ul class="install-groups">
              {progress.groups.map((g) => (
                <li key={g.label} class={g.done >= g.bytes ? "done" : ""}>
                  <span class="label">{g.label}</span>
                  <progress max={g.bytes} value={g.done} aria-label={`${g.label} download`} />
                  <span class="muted num">{formatBytes(g.done)} / {formatBytes(g.bytes)}</span>
                </li>
              ))}
            </ul>
          </>
        ) : !error ? (
          <p class="muted" role="status">Reading the Snapshot manifest…</p>
        ) : null}
        {error && (
          <div class="install-error" role="alert">
            <p>The download stopped: {error}</p>
            <p class="muted">What arrived is kept; retrying downloads only what is missing.</p>
            <button type="button" class="primary" onClick={() => setAttempt(attempt + 1)}>Retry</button>
          </div>
        )}
      </div>
    </div>
  );
}
